import { useFormik } from 'formik';
import { ChangeEvent, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import useMergeGroups from '../../../../API/hooks/groups/useMergeGroups';
import { MergeGroupsRequest } from '../../../../API/types/Groups';
import Input from '../../../../components/common/Input/Input';
import { Button } from '../../../../components/ui/Button/Button';
import CheckBox from '../../../../components/common/CheckBox/CheckBox';
import S from './MergeGroupStyles';

type Props = {
  fleetId: string;
  groupIds: string[];
  toggleModal: (closed: boolean) => void;
  onSuccess?: () => void;
};

function MergeGroup({
  fleetId, groupIds, toggleModal, onSuccess,
}: Props): JSX.Element {
  const [submitError, setSubmitError] = useState<string | string[] | null>(null);
  const onSubmitFail = (errorMessage: string): void => setSubmitError(errorMessage);
  const onSubmitSuccess = (): void => {
    toast.success('Groups has been cloned successfully');
    toggleModal(false);
    if (onSuccess) onSuccess();
  };

  const { mutate: mergeGroups } = useMergeGroups(
    onSubmitSuccess,
    onSubmitFail,
  );

  const formik = useFormik({
    initialValues: {
      name: '',
      mergeUsers: false,
      deleteOriginGroup: false,
    },
    onSubmit: (values) => mergeGroups({
      ...values,
      groupIds,
      fleetId,
    } as unknown as MergeGroupsRequest),
    validate: (values) => {
      const errors = {} as Record<string, string | undefined>;

      if (!values.name.length) errors.name = 'Required';
      return errors;
    },
    validateOnMount: true,
  });

  // if there is onSubmit error formik will stack in submitting state
  // blocking further interactions with submit button
  useEffect(() => {
    formik.setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitError]);

  const handleInputChange = (e: ChangeEvent<any>): void => {
    formik.handleChange(e);
    setSubmitError('');
  };

  const handleCheckboxChange = (id: string, state: boolean): void => {
    formik.setFieldValue(id, state);
    formik.setSubmitting(false);
    setSubmitError('');
  };

  return (
    <S.Wrapper>
      <form onSubmit={formik.handleSubmit}>
        <div className="details-container">
          <S.RowContainer>
            <Input
              label="Name"
              name="name"
              placeholder="Type group name"
              onChange={handleInputChange}
              onBlur={formik.handleBlur}
              value={formik.values.name}
              error={formik.touched.name && formik.errors.name}
            />
          </S.RowContainer>
          <CheckBox
            label="Copy existing users"
            name="mergeUsers"
            state={formik.values.mergeUsers}
            onChange={handleCheckboxChange}
          />
          <CheckBox
            label="Delete original groups"
            name="deleteOriginGroup"
            state={formik.values.deleteOriginGroup}
            onChange={handleCheckboxChange}
          />
          <S.RowContainer className="buttons-container">
            <Button
              height="50px"
              onClick={() => toggleModal(false)}
              type="button"
            >
              Cancel
            </Button>
            <Button
              className="add-button"
              height="50px"
              type="submit"
              disabled={formik.isSubmitting || !formik.isValid || !!submitError}
            >
              Merge
            </Button>
          </S.RowContainer>
          {!!submitError && (
            typeof submitError === 'string'
              ? <S.ErrorMessage>{submitError}</S.ErrorMessage>
              : submitError.map((error) => <S.ErrorMessage>{error}</S.ErrorMessage>)
          )}
        </div>
      </form>
    </S.Wrapper>
  );
}

export default MergeGroup;
